<script lang="ts">
/**
 * Field · first record · second record · merged record. Exported so the
 * dialog's column header lines up with the rows under it.
 */
export const GAIA_MERGE_COLUMNS =
  'minmax(0, 0.75fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.15fr)';
</script>

<script setup lang="ts">
import { computed, useId, type PropType } from 'vue';
import type { SelectOption } from 'naive-ui';
import GaiaAutoComplete from '../../base/inputs/GaiaAutoComplete.vue';
import GaiaInput from '../../base/inputs/GaiaInput.vue';
import GaiaSelect from '../../base/selects/GaiaSelect.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaTextarea from '../../base/inputs/GaiaTextarea.vue';
import GaiaDatePicker from '../../base/inputs/GaiaDatePicker.vue';
import GaiaTagInput from '../../base/inputs/GaiaTagInput.vue';
import { gaiaNeutral } from '../../themes/tokens';
import { joinTags, splitTags } from './mergeTags';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  /** identical = same in both records; differs = conflict; missing = required with no result value yet. */
  state: {
    type: String as PropType<'identical' | 'differs' | 'missing' | undefined>,
    default: undefined,
  },
  leftValue: {
    type: String,
    default: '',
  },
  rightValue: {
    type: String,
    default: '',
  },
  /** The editable merged result (v-model:value). */
  value: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  /**
   * Which control edits the merged result. Mirrors `EntityField['kind']` so a
   * config's field kind can be handed straight over: a field the create dialog
   * shows as a dropdown must not degrade to free text here, or the merge writes
   * a value the enum doesn't have. `tags` edits a chip list; its value stays a
   * comma-joined string (`mergeTags.ts`) like every other row's.
   */
  kind: {
    type: String as PropType<'text' | 'email' | 'textarea' | 'select' | 'date' | 'tags'>,
    default: 'text',
  },
  /** Options for `kind: 'select'` — straight off the generated enum. */
  options: {
    type: Array as PropType<SelectOption[]>,
    default: undefined,
  },
  /** Upper-cases as you type — country codes. */
  upper: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: undefined,
  },
  /** A text result offers these as you type, but takes whatever is written. */
  suggestions: {
    type: Array as PropType<string[]>,
    default: undefined,
  },
});

const emit = defineEmits<{ 'update:value': [value: string]; 'copy-left': []; 'copy-right': [] }>();

const emptyLabel = '—';
/** A select shows its option's label ("Germany"), not the stored code ("DEU"). */
function display(value: string): string {
  if (!value) return emptyLabel;
  const option = props.options?.find(candidate => candidate.value === value);
  return typeof option?.label === 'string' ? option.label : value;
}
const leftDisplay = computed(() => display(props.leftValue));
const rightDisplay = computed(() => display(props.rightValue));

/**
 * The two records disagree, so each side is a choice (a radio). Where they
 * agree, or only one has a value, there is nothing to choose between and the
 * values read as plain text.
 */
const isChoice = computed(() => props.leftValue !== props.rightValue);
const leftChosen = computed(() => Boolean(props.value) && props.value === props.leftValue);
const rightChosen = computed(() => Boolean(props.value) && props.value === props.rightValue);
/** Both radios of a row share a name, so the pair behaves as one group. */
const groupName = `merge-${useId()}`;

function choose(side: 'left' | 'right'): void {
  if (side === 'left') emit('copy-left');
  else emit('copy-right');
}

function onValue(value: string): void {
  emit('update:value', value);
}

const rowBorder = gaiaNeutral.borderSoft;
const chosenSurface = gaiaNeutral.surfaceTint;
</script>

<template>
  <!-- One row of the merge dialog: the field, each record's value, and the
       editable merged value. Where the records differ their values are radios
       — picking one fills the merged value; typing over it picks neither. -->
  <div class="merge-row" :style="{ gridTemplateColumns: GAIA_MERGE_COLUMNS }">
    <n-flex vertical :size="2" :style="{ minWidth: 0 }">
      <gaia-text size="dense" weight="medium" tone="text">
        {{ props.label }}<gaia-text v-if="props.required" tone="alert"> *</gaia-text>
      </gaia-text>
      <gaia-text v-if="props.state === 'missing'" size="micro" tone="alert">
        Pick or type a value
      </gaia-text>
    </n-flex>

    <div
      v-for="side in ['left', 'right'] as const"
      :key="side"
      class="source"
      :class="{ chosen: isChoice && (side === 'left' ? leftChosen : rightChosen) }"
    >
      <n-radio
        v-if="isChoice && (side === 'left' ? props.leftValue : props.rightValue)"
        :name="groupName"
        :checked="side === 'left' ? leftChosen : rightChosen"
        @update:checked="choose(side)"
      >
        <gaia-text size="dense" tone="ink" class="source-text">
          {{ side === 'left' ? leftDisplay : rightDisplay }}
        </gaia-text>
      </n-radio>
      <gaia-text
        v-else
        size="dense"
        :tone="(side === 'left' ? props.leftValue : props.rightValue) ? 'tertiary' : 'faint'"
        class="source-text"
      >
        {{ side === 'left' ? leftDisplay : rightDisplay }}
      </gaia-text>
    </div>

    <gaia-select
      v-if="props.kind === 'select'"
      :value="props.value || null"
      :options="props.options ?? []"
      :placeholder="props.placeholder"
      :aria-label="`Merged ${props.label}`"
      style="width: 100%"
      @update:value="(value: string | null) => onValue(value ?? '')"
    />
    <gaia-date-picker
      v-else-if="props.kind === 'date'"
      :formatted-value="props.value || null"
      value-format="yyyy-MM-dd"
      type="date"
      :aria-label="`Merged ${props.label}`"
      style="width: 100%"
      @update:formatted-value="(value: string | null) => onValue(value ?? '')"
    />
    <gaia-tag-input
      v-else-if="props.kind === 'tags'"
      :value="splitTags(props.value)"
      :placeholder="props.placeholder"
      :aria-label="`Merged ${props.label}`"
      style="width: 100%"
      @update:value="(value: string[]) => onValue(joinTags(value))"
    />
    <gaia-textarea
      v-else-if="props.kind === 'textarea'"
      :value="props.value"
      :placeholder="props.placeholder"
      :aria-label="`Merged ${props.label}`"
      :autosize="{ minRows: 2 }"
      @update:value="onValue"
    />
    <gaia-auto-complete
      v-else-if="props.suggestions"
      :value="props.value"
      :suggestions="props.suggestions"
      :placeholder="props.placeholder"
      :input-props="{ 'aria-label': `Merged ${props.label}` }"
      style="width: 100%"
      @update:value="onValue"
    />
    <gaia-input
      v-else
      :value="props.value"
      :placeholder="props.placeholder"
      :aria-label="`Merged ${props.label}`"
      :maxlength="props.maxLength"
      :input-props="{ style: props.upper ? 'text-transform: uppercase' : undefined }"
      style="width: 100%"
      @update:value="onValue"
    />
  </div>
</template>

<style scoped>
/* A table row: four columns of mixed fractions that the dialog's header
   shares (GAIA_MERGE_COLUMNS) — n-grid's uniform spans can't express it. */
.merge-row {
  display: grid;
  gap: 16px;
  align-items: center;
  padding: 9px 4px;
  border-bottom: 1px solid v-bind(rowBorder);
}

/* A record's value. The chosen one sits on a faint wash so the row reads as
   "this side wins" without a colour of its own. */
.source {
  min-width: 0;
  padding: 6px 10px;
  border-radius: 8px;
}

.source.chosen {
  background: v-bind(chosenSurface);
}

.source-text {
  word-break: break-word;
}
</style>
